import { ArrowLeft2 } from 'iconsax-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useRouteLoaderData } from 'react-router';
import { useTypedParams, useTypedSearchParams, useTypedState } from 'react-router-typesafe-routes';
import ConversationVirtuaList from '@/components/conversations/conversation-list/conversation-virtua-list';
import ConversationProviders from '@/components/conversations/conversation-providers';
import MessageStateIndicator from '@/components/conversations/message-state-indicator';
import SessionSettingMenu from '@/components/conversations/session-setting-menu';
import { WebSocketStateIndicator } from '@/components/conversations/web-socket-state-indicator';
import AppHeader from '@/components/layouts/page-layout/app-header';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useIsAdminSession from '@/hooks/conversations/use-is-admin-session';
import { useSessionMessages } from '@/hooks/sessions/use-session-messages';
import useResetScrollYOnFocus from '@/hooks/use-reset-scroll-y-on-focus';
import useRotateResize from '@/hooks/use-rotate-resize';
import useUserPlatform, { useShouldRetractRightSection } from '@/hooks/use-user-platform';
import { useIsInternalUser } from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';
import type { SessionMessagesLayoutLoaderReturnType } from '@/routes/messages/sessions/_layout.loader';
import { createConversationStore } from '@/stores/conversation';
import { useEmbedLayout } from '@/stores/device';
import { useMessageStore } from '@/stores/message';
import { EmbedLayout } from '@/utils/embed-layout';

interface Props {
  sessionId: number;
}

export function SessionMessages({ sessionId }: Props) {
  const layoutLoaderData = useRouteLoaderData<SessionMessagesLayoutLoaderReturnType>(ROUTES.MESSAGES.SESSIONS.$path());
  const [{ fromUnread }] = useTypedSearchParams(ROUTES.MESSAGES.SESSIONS);
  const { from: navigatedFrom } = useTypedState(ROUTES.MESSAGES.SESSIONS);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDesktop, isEmbedded } = useUserPlatform();

  const { searchId, listKey } = useMessageStore();
  const { session, friendInfo } = useSessionMessages(sessionId);
  const { isInternalUser } = useIsInternalUser();
  const isAdminSession = useIsAdminSession('session', sessionId);
  const layout = useEmbedLayout();
  const { dispatch } = useCommDispatcher();
  const shouldRetractRightSection = useShouldRetractRightSection();

  const store = useMemo(
    () => createConversationStore('session', sessionId, isAdminSession),
    [sessionId, isAdminSession],
  );

  const promise = useMemo(() => layoutLoaderData?.promises || false, [sessionId]);

  useResetScrollYOnFocus();
  useRotateResize();

  const handleNavigateBack = () => {
    if (isEmbedded) {
      dispatch({
        event: 'exit-session',
        data: { type: 'session', id: sessionId },
      });

      if (navigatedFrom === 'comm') dispatch({ event: 'dismiss' });
    }
  };

  return (
    <PageLayout
      title={
        friendInfo?.FriendInfo.RemarkName ||
        friendInfo?.FriendInfo.FriendNickName ||
        session?.NickName ||
        t('nav.messages')
      }
      className='gap-0 pb-safe-or-0 has-[.ql-editor:focus]:mode-standalone:pb-0'>
      {layout >= EmbedLayout.COMPACT && (
        <AppHeader
          navigateBack={isDesktop ? undefined : Boolean(searchId || navigatedFrom === 'search') || true}
          onNavigateBack={handleNavigateBack}
          className='desktop:px-6 desktop:border-b border-base-200 mb-0'
          actions={
            <>
              <MessageStateIndicator />
              <WebSocketStateIndicator />
              <SessionSettingMenu
                type='session'
                id={sessionId}
              />
            </>
          }>
          {isDesktop && shouldRetractRightSection && (
            <button
              type='button'
              className='btn btn-ghost btn-square mr-2'
              onClick={() => {
                navigate(-1);
                useMessageStore.getState().resetKey();
              }}>
              <ArrowLeft2
                variant='Linear'
                className='size-6'
              />
            </button>
          )}
          {(session || friendInfo) && (
            <Link
              className='w-full desktop:w-fit'
              to={
                isDesktop && friendInfo?.FriendInfo.FriendID
                  ? ROUTES.CONTACT.USERS.BY_ID.$buildPath({
                      params: { userId: friendInfo.FriendInfo.FriendID },
                    })
                  : ROUTES.MESSAGES.SESSIONS.$.SETTINGS.$path({ relative: true })
              }>
              <div className='flex flex-col w-full  '>
                <h3 className='line-clamp-1 flex-1 break-all text-base font-bold'>
                  {friendInfo?.FriendInfo.RemarkName || friendInfo?.FriendInfo.FriendNickName || session?.NickName}
                </h3>

                {isInternalUser && friendInfo?.FriendInfo.OnlineDesc && (
                  <div className='flex items-center gap-1'>
                    {friendInfo?.FriendInfo.IsOnline && <div className='w-1.5 h-1.5 bg-green-500 rounded-full' />}
                    <span className='text-xs text-metal-500'>{friendInfo?.FriendInfo.OnlineDesc}</span>
                  </div>
                )}
              </div>
            </Link>
          )}
        </AppHeader>
      )}

      <ConversationProviders
        type='session'
        id={sessionId}
        store={store}>
        <LoadingLayout
          className='bg-base-100'
          promise={promise}>
          {() =>
            session && (
              <ConversationVirtuaList
                key={listKey}
                searchId={searchId}
                initialUnreadCount={layoutLoaderData?.initialUnreadCount}
                lastMsgID={session.LastMsgID}
                lastReadId={layoutLoaderData?.lastReadId}
                isFromUnread={Boolean(fromUnread)}
              />
            )
          }
        </LoadingLayout>
      </ConversationProviders>
    </PageLayout>
  );
}

export default function SessionMessagesByIdRoute() {
  const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS);
  const layout = useEmbedLayout();
  const { session, friendInfo } = useSessionMessages(id);

  if (layout === EmbedLayout.MINI && (!session || !friendInfo))
    return (
      <div className='m-auto flex flex-col gap-y-24 flex-center'>
        <span className='loading loading-spinner loading-lg text-primary' />
      </div>
    );

  return <SessionMessages sessionId={id} />;
}
